import React, { useContext, useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import productData from "../Datas/productData";
import "../css/productdetails.scss";
import { CartContext } from "../Component/CartContext";
import {
  FaStar,
  FaChevronDown,
  FaTruck,
  FaLeaf,
  FaCommentDots,
} from "react-icons/fa";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find((item) => item.id === parseInt(id));
  const { addToCart } = useContext(CartContext);

  const [selectedVariants, setSelectedVariants] = useState({});
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("info");

  useEffect(() => {
    if (product) {
      // Initialize other variants from product.variants array
      const initialVariants = {};
      product.variants?.forEach((variant) => {
        initialVariants[variant.type] = variant.options[0];
      });
      setSelectedVariants(initialVariants);

      // Initialize color separately as it's often a top-level array in your data
      setSelectedColor(product.colors?.[0] || "");

      setSelectedImage(0);
      setQuantity(1);
    }
  }, [product]);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!product) {
    return (
      <div className="product-detail-not-found">
        <h2>Product not found</h2>
        <Link onClick={toTop} to="/shop">
          Back to Shop
        </Link>
      </div>
    );
  }

  const handleVariantChange = (type, value) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const handleAddToCart = () => {
    // Merge all selected variant values into a string for the context
    const variantString = Object.values(selectedVariants).join(" / ");
    addToCart(product.id, quantity, variantString, selectedColor);
    navigate("/cartpage");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="product-detail-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/" onClick={toTop}>
            Home
          </Link>
          <span className="separator">|</span>
          <Link to="/shop" onClick={toTop}>
            All Hardware
          </Link>
          <span className="separator">|</span>
          <span className="current">{product.name}</span>
        </nav>

        <div className="product-main">
          {/* Left: Image Gallery */}
          <div className="product-gallery">
            <div className="thumbnail-list">
              {product.images?.map((img, idx) => (
                <div
                  key={idx}
                  className={`thumbnail-item ${
                    selectedImage === idx ? "active" : ""
                  }`}
                  onClick={() => setSelectedImage(idx)}
                >
                  <img src={img} alt={`${product.name} thumbnail ${idx}`} />
                </div>
              ))}
            </div>
            <div className="main-image">
              {product.id >= 19 && <span className="badge-new">NEW</span>}
              <img
                src={product.images?.[selectedImage] || product.image}
                alt={product.name}
              />
            </div>
          </div>

          {/* Right: Product Info */}
          <div className="product-info-panel">
            <div className="brand-name">
              {product.collection || "Pedarcircle"}
            </div>
            <h1 className="product-title">{product.name}</h1>
            <div className="sku">SKU: {product.sku}</div>

            <div className="price-row">
              <span className="current-price">${product.price}</span>
              {product.oldPrice && (
                <>
                  <span className="old-price">${product.oldPrice}</span>
                  <span className="discount-tag">-{product.discount}</span>
                </>
              )}
              <div className="stars-row">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} />
                ))}
                <span className="review-count">({product.reviews || 1})</span>
              </div>
            </div>

            <p className="short-desc">{product.description?.[0]}</p>

            {/* Variants Section */}
            <div className="variants-section">
              {/* Dynamic Variants (Size, Length, Type, etc.) */}
              {product.variants?.map((variant) => (
                <div className="variant-group" key={variant.type}>
                  <label>
                    {variant.type}: {selectedVariants[variant.type]}
                  </label>
                  <div className="custom-dropdown">
                    <select
                      value={selectedVariants[variant.type] || ""}
                      onChange={(e) =>
                        handleVariantChange(variant.type, e.target.value)
                      }
                    >
                      {variant.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <FaChevronDown className="icon" />
                  </div>
                </div>
              ))}

              {/* Color Variant */}
              {product.colors && product.colors.length > 0 && (
                <div className="variant-group">
                  <label>Colour: {selectedColor}</label>
                  <div className="custom-dropdown">
                    <select
                      value={selectedColor}
                      onChange={(e) => setSelectedColor(e.target.value)}
                    >
                      {product.colors.map((color) => (
                        <option key={color} value={color}>
                          {color}
                        </option>
                      ))}
                    </select>
                    <FaChevronDown className="icon" />
                  </div>
                </div>
              )}
            </div>

            <div className="stock-status">
              <span className="dot"></span> Available in stock (100+)
            </div>

            <div className="purchase-actions">
              <div className="quantity-selector">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>
                  -
                </button>
                <span>{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)}>+</button>
              </div>
              <button className="add-to-basket" onClick={handleAddToCart}>
                Add To Basket
              </button>
            </div>

            <div className="trust-badges">
              <div className="badge-item">
                <FaTruck /> <span>Free UK Standard Shipping over £75</span>
              </div>
              <div className="badge-item">
                <FaLeaf /> <span>Certified B Corp</span>
              </div>
              <div className="badge-item">
                <FaCommentDots /> <span>Over 3,000 5 Star Reviews</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Tabs */}
        <div className="product-details-tabs">
          <div className="tabs-header">
            <button
              className={activeTab === "info" ? "active" : ""}
              onClick={() => setActiveTab("info")}
            >
              Product Information
            </button>
            <button
              className={activeTab === "delivery" ? "active" : ""}
              onClick={() => setActiveTab("delivery")}
            >
              Delivery & Returns
            </button>
          </div>

          <div className="tab-content">
            {activeTab === "info" ? (
              <div className="info-table">
                {Object.entries(product.productInformation || {}).map(
                  ([key, value]) => (
                    <div className="table-row" key={key}>
                      <div className="label">
                        {key
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase())}
                      </div>
                      <div className="value">{value}</div>
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="delivery-info">
                <div className="delivery-grid">
                  <div className="column">
                    <h3>How long does delivery take?</h3>
                    <p>
                      <strong>Standard:</strong> Within 5 business days, sent
                      with Royal Mail for £4.95 (Free for UK orders over £75)
                    </p>
                    <p>
                      <strong>Express:</strong> Within 3 business days, sent
                      with DPD for £6.95 (Free for UK orders over £500)
                    </p>
                  </div>
                  <div className="column">
                    <h3>What is your returns policy?</h3>
                    <p>
                      Any unused items in their original condition and packaging
                      can be returned within 60 days of delivery.{" "}
                    </p>
                    <h3>Pre-Orders</h3>
                    <p>
                      Items listed on Pre-Order have an estimated restock date,
                      which is subject to delays.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
